import React, { useState } from 'react';
import {
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewListIcon from '@mui/icons-material/ViewList';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import BreadcrumbsBar from './BreadcrumbsBar';
import AddDropdown from './AddDropdown';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { ViewMode } from '../hooks/useViewMode';

/**
 * ContentHeader: Horizontal bar with BreadcrumbsBar on the left and
 * action buttons (AddDropdown, Refresh, View Mode) pinned to the right.
 *
 * On mobile (< md): actions collapse into an overflow (⋮) menu to give
 * breadcrumbs maximum space.
 *
 * Requirements: 6.1, 6.2, 6.6, 6.8, 11.3, 14.5, 14.7
 */

interface ContentHeaderProps {
  onAddBookmark?: () => void;
  onAddCategory?: () => void;
  onRefresh?: () => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

const ContentHeader: React.FC<ContentHeaderProps> = ({
  onAddBookmark,
  onAddCategory,
  onRefresh,
  viewMode,
  onViewModeChange,
}) => {
  const { user } = useWebAuthn();
  const { isOnline } = useOnlineStatus();
  const cache = useCacheStore();
  const timestamp = cache.getTimestamp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);

  const formatSyncTime = (ts: string | null): string => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const handleAddBookmark = () => {
    setOverflowAnchor(null);
    onAddBookmark?.();
  };

  const handleAddCategory = () => {
    setOverflowAnchor(null);
    onAddCategory?.();
  };

  const handleRefresh = () => {
    setOverflowAnchor(null);
    onRefresh?.();
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setOverflowAnchor(null);
    onViewModeChange?.(mode);
  };

  // Desktop: full action bar
  const renderDesktopActions = () => (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {viewMode && onViewModeChange && (
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, value) => { if (value) onViewModeChange(value); }}
          size="small"
          aria-label="View mode"
          sx={{ mr: 0.5 }}
        >
          <ToggleButton value="card" aria-label="Card view" sx={{ p: 0.5 }}>
            <Tooltip title="Card view"><ViewModuleIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="compact" aria-label="Compact view" sx={{ p: 0.5 }}>
            <Tooltip title="Compact view"><ViewStreamIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="List view" sx={{ p: 0.5 }}>
            <Tooltip title="List view"><ViewListIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {user && (
        <AddDropdown
          onAddBookmark={handleAddBookmark}
          onAddCategory={handleAddCategory}
          disabled={!isOnline}
        />
      )}

      <Tooltip title="Refresh">
        <span>
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={!isOnline}
            aria-label="Refresh"
          >
            <RefreshIcon />
          </IconButton>
        </span>
      </Tooltip>
      {timestamp && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
          {formatSyncTime(timestamp)}
        </Typography>
      )}
    </Box>
  );

  // Mobile: overflow menu
  const renderMobileActions = () => (
    <>
      <IconButton
        size="small"
        onClick={(e) => setOverflowAnchor(e.currentTarget)}
        aria-label="More actions"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={overflowAnchor}
        open={Boolean(overflowAnchor)}
        onClose={() => setOverflowAnchor(null)}
      >
        {/* View mode options */}
        <MenuItem
          selected={viewMode === 'card'}
          onClick={() => handleViewModeChange('card')}
        >
          <ListItemIcon><ViewModuleIcon fontSize="small" /></ListItemIcon>
          Card view
        </MenuItem>
        <MenuItem
          selected={viewMode === 'compact'}
          onClick={() => handleViewModeChange('compact')}
        >
          <ListItemIcon><ViewStreamIcon fontSize="small" /></ListItemIcon>
          Compact view
        </MenuItem>
        <MenuItem
          selected={viewMode === 'list'}
          onClick={() => handleViewModeChange('list')}
        >
          <ListItemIcon><ViewListIcon fontSize="small" /></ListItemIcon>
          List view
        </MenuItem>

        {/* Add actions */}
        {user && (
          <MenuItem onClick={handleAddBookmark} disabled={!isOnline}>
            <ListItemIcon><BookmarkAddIcon fontSize="small" /></ListItemIcon>
            Add Bookmark
          </MenuItem>
        )}
        {user && (
          <MenuItem onClick={handleAddCategory} disabled={!isOnline}>
            <ListItemIcon><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
            Add Category
          </MenuItem>
        )}

        {/* Refresh */}
        <MenuItem onClick={handleRefresh} disabled={!isOnline}>
          <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
          Refresh
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        py: 0.5,
        px: 1,
      }}
    >
      {/* BreadcrumbsBar takes remaining space, overflow hidden */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <BreadcrumbsBar />
      </Box>

      {/* Desktop: full toolbar. Mobile: overflow menu */}
      {isMobile ? renderMobileActions() : renderDesktopActions()}
    </Box>
  );
};

export default ContentHeader;
